import { Controller } from "@hotwired/stimulus"
import { NativeBridge } from "../lib/native_bridge"
import {
  registerToken,
  unregisterToken,
  checkRegistration,
  getPlatform
} from "../lib/native_push_service"

/**
 * NativePushController - Push notification handling for native apps
 *
 * Manages FCM token registration and notification permission requests
 * for Hotwire Native apps. Uses NativePushService for API calls.
 *
 * I18n: All user-facing strings passed via data attributes
 *
 * Usage:
 *   <div data-controller="native-push"
 *        data-native-push-register-url-value="/api/native/push_tokens"
 *        data-native-push-i18n-enabled-value="已開啟通知"
 *        data-native-push-i18n-disabled-value="已關閉通知"
 *        data-native-push-i18n-denied-value="通知權限被拒絕"
 *        data-native-push-i18n-loading-value="處理中...">
 */
export default class extends Controller {
  static targets = ["button", "status", "toggle"]

  static values = {
    registerUrl: { type: String, default: "/api/native/push_tokens" },
    unregisterUrl: { type: String, default: "/api/native/push_tokens" },
    autoRegister: { type: Boolean, default: false },
    // I18n values (passed from server via data attributes)
    i18nEnabled: { type: String, default: "" },
    i18nDisabled: { type: String, default: "" },
    i18nDenied: { type: String, default: "" },
    i18nLoading: { type: String, default: "" },
    // ✅ NEW: 동기화 상태
    i18nSyncing: { type: String, default: "" },      // "同步中..."
    i18nSyncError: { type: String, default: "" },    // "同步失敗"
    // ✅ NEW: 에러 메시지
    i18nErrorNetwork: { type: String, default: "" },   // "網路錯誤，請稍後再試"
    i18nErrorAuth: { type: String, default: "" },      // "請重新登入"
    i18nErrorDuplicate: { type: String, default: "" }, // "此裝置已註冊至其他帳號"
    i18nErrorServer: { type: String, default: "" },    // "伺服器錯誤，請稍後再試"
    i18nErrorGeneric: { type: String, default: "" },   // "發生錯誤"
    // ✅ NEW: 설정 앱 안내
    i18nOpenSettings: { type: String, default: "" },   // "前往設定開啟通知"
    i18nSettingsPrompt: { type: String, default: "" }  // "通知權限已被拒絕。請到設定中開啟通知。"
  }

  connect() {
    this.isNative = NativeBridge.isNative()

    if (!this.isNative) {
      // Hide push controls for non-native apps (use web push instead)
      this.element.style.display = "none"
      return
    }

    // Listen for token refresh events
    this.tokenRefreshUnsubscribe = NativeBridge.Push.onTokenRefresh((data) => {
      this.handleTokenRefresh(data.token)
    })

    // Listen for notification events
    this.notificationUnsubscribe = NativeBridge.Push.onNotification((data) => {
      this.handleNotification(data)
    })

    if (this.autoRegisterValue) {
      this.autoRegister()
    }

    // Check initial toggle state
    this.checkInitialState()
  }

  disconnect() {
    if (this.tokenRefreshUnsubscribe) {
      this.tokenRefreshUnsubscribe()
    }
    if (this.notificationUnsubscribe) {
      this.notificationUnsubscribe()
    }
  }

  /**
   * Check permission status and update toggle state (Permission-First Approach)
   *
   * Toggle state = Permission status (token exists), NOT server registration status
   * Server registration happens in background and doesn't affect toggle state
   */
  async checkInitialState() {
    if (!this.hasToggleTarget) return

    try {
      const result = await NativeBridge.Push.getToken()

      if (result.token) {
        // ✅ Token exists = Permission granted → Toggle ON
        this.setToggleState(true)

        // Background: Check server registration and auto-register if needed
        const registration = await checkRegistration({
          endpoint: this.registerUrlValue
        })

        if (registration.registered) {
          // Already registered → Show success status
          this.setStatus(this.i18nEnabledValue)
        } else if (!registration.error) {
          // Not registered + No error → Auto-register in background
          this.setStatus(this.i18nSyncingValue || this.getFallbackText('syncing'))
          await this.handleTokenRefresh(result.token)
          // handleTokenRefresh will update status after registration
        } else {
          // Registration check failed → Show sync error (Toggle stays ON)
          console.error("[NativePush] Registration check failed:", registration.error)
          this.setStatus(this.i18nSyncErrorValue || this.getFallbackText('syncError'))
        }
      } else {
        // ❌ No token = Permission not granted → Toggle OFF
        this.setToggleState(false)
        this.setStatus(this.i18nDisabledValue)
      }
    } catch (error) {
      console.error("[NativePush] checkInitialState error:", error)
      this.setToggleState(false)
    }
  }

  /**
   * Map error codes/messages to localized I18n strings
   * ✅ Backend error codes first (structured), string matching fallback (backward compatible)
   */
  getErrorMessage(error) {
    if (!error) return this.getFallbackText('generic')

    // ✅ PRIMARY: Backend error codes (structured, reliable)
    // Backend sends: { error: "message", code: "ERROR_CODE" }
    // Rails controller should return structured error responses with code field
    const errorCodeMap = {
      // Network errors
      'NETWORK_ERROR': this.i18nErrorNetworkValue,
      'FETCH_FAILED': this.i18nErrorNetworkValue,
      'TIMEOUT': this.i18nErrorNetworkValue,
      // Authentication errors
      'UNAUTHORIZED': this.i18nErrorAuthValue,
      'AUTHENTICATION_FAILED': this.i18nErrorAuthValue,
      // Duplicate registration errors
      'DUPLICATE_TOKEN': this.i18nErrorDuplicateValue,
      'TOKEN_ALREADY_REGISTERED': this.i18nErrorDuplicateValue,
      // Server/validation errors (from push_tokens_controller.rb)
      'VALIDATION_ERROR': this.i18nErrorServerValue,
      'NOT_FOUND': this.i18nErrorServerValue,
      'INVALID_PLATFORM': this.i18nErrorServerValue,
      'MISSING_TOKEN': this.i18nErrorServerValue,
      'SERVER_ERROR': this.i18nErrorServerValue,
      'INTERNAL_ERROR': this.i18nErrorServerValue
    }

    // Check for structured error code from backend
    if (error.code && errorCodeMap[error.code]) {
      return errorCodeMap[error.code] || this.getFallbackText(error.code.toLowerCase())
    }

    // ✅ FALLBACK: String pattern matching (backward compatibility)
    // Used when backend doesn't provide structured error codes yet
    const errorString = error.toString().toLowerCase()

    if (errorString.includes('network') || errorString.includes('fetch')) {
      return this.i18nErrorNetworkValue || this.getFallbackText('network')
    }

    if (errorString.includes('unauthorized') || errorString.includes('401')) {
      return this.i18nErrorAuthValue || this.getFallbackText('auth')
    }

    if (errorString.includes('duplicate') || errorString.includes('already')) {
      return this.i18nErrorDuplicateValue || this.getFallbackText('duplicate')
    }

    if (errorString.includes('500') || errorString.includes('server')) {
      return this.i18nErrorServerValue || this.getFallbackText('server')
    }

    // 기본값: Return original message if I18n not available
    return error.toString()
  }

  /**
   * Fallback text for missing I18n values
   * ✅ English fallback (not hardcoded Chinese)
   * @param {string} key - Error key (network, auth, duplicate, server, generic)
   * @returns {string} Fallback error message
   */
  getFallbackText(key) {
    const fallbacks = {
      syncing: "Syncing...",
      syncError: "Sync failed",
      network: "Network error, please try again",
      auth: "Please log in again",
      duplicate: "This device is registered to another account",
      server: "Server error, please try again",
      generic: "An error occurred",
      // Settings prompt (for permanently denied permission)
      settingsPrompt: "Notification permission denied.",
      openSettings: "Open Settings"
    }
    return fallbacks[key] || fallbacks.generic
  }

  /**
   * Toggle handler - enable or disable push notifications
   */
  async toggle(event) {
    if (event.target.checked) {
      await this.requestPermission()
    } else {
      await this.unregister()
    }
  }

  /**
   * Auto-register if permission already granted
   */
  async autoRegister() {
    try {
      const result = await NativeBridge.Push.getToken()
      if (result.token) {
        await this.handleTokenRefresh(result.token)
      }
    } catch (error) {
      // Permission not granted yet, wait for user action
      console.log("[NativePush] Token not available, waiting for permission")
    }
  }

  /**
   * Request push notification permission
   *
   * Enhanced flow:
   * 1. Check current permission status first
   * 2. If already granted → register token
   * 3. If not granted → request permission
   * 4. If denied → show settings prompt (user can manually enable in settings)
   */
  async requestPermission() {
    if (!this.isNative) return

    this.setLoading(true)

    try {
      // Step 1: Check current permission status
      const status = await NativeBridge.Push.getPermissionStatus()

      if (status.granted) {
        // Already granted → just get token and register
        const tokenResult = await NativeBridge.Push.getToken()
        if (tokenResult.token) {
          await this.registerTokenAndUpdateUI(tokenResult.token)
        }
        return
      }

      // Step 2: Request permission (not yet granted)
      const result = await NativeBridge.Push.requestPermission()

      if (result.granted && result.token) {
        await this.registerTokenAndUpdateUI(result.token)
      } else {
        // Permission denied → show settings prompt
        this.showSettingsPrompt()
      }
    } catch (error) {
      console.error("[NativePush] Permission error:", error)
      const errorMessage = this.getErrorMessage(error.message || error)
      this.setError(errorMessage)
      this.setToggleState(false)
      this.dispatch("error", { detail: { error: errorMessage } })
    } finally {
      this.setLoading(false)
    }
  }

  /**
   * Register token with server and update UI
   * @param {string} token - FCM token
   */
  async registerTokenAndUpdateUI(token) {
    const registration = await registerToken(token, {
      endpoint: this.registerUrlValue
    })

    if (registration.success) {
      this.setSuccess(this.i18nEnabledValue)
      this.setToggleState(true)
      this.dispatch("enabled", { detail: { token } })
    } else {
      const errorMessage = this.getErrorMessage(registration.error)
      throw new Error(errorMessage)
    }
  }

  /**
   * Show prompt to open settings when permission is denied
   * User can manually enable notifications in app settings
   */
  showSettingsPrompt() {
    const promptMessage = this.i18nSettingsPromptValue ||
      this.getFallbackText('settingsPrompt')
    const openSettingsText = this.i18nOpenSettingsValue ||
      this.getFallbackText('openSettings')

    // Update UI to show denied state with settings link
    this.setError(promptMessage)
    this.setToggleState(false)
    this.dispatch("denied", { detail: { showSettings: true } })

    // Add clickable link to open settings (if status target exists)
    if (this.hasStatusTarget) {
      const settingsLink = document.createElement('button')
      settingsLink.textContent = openSettingsText
      settingsLink.className = 'text-primary-600 underline ml-2 text-sm'
      settingsLink.addEventListener('click', async (e) => {
        e.preventDefault()
        try {
          await NativeBridge.Push.openSettings()
        } catch (error) {
          console.error("[NativePush] Failed to open settings:", error)
        }
      })
      this.statusTarget.appendChild(settingsLink)
    }
  }

  /**
   * Handle token refresh from native app
   */
  async handleTokenRefresh(token) {
    const result = await registerToken(token, {
      endpoint: this.registerUrlValue
    })

    if (result.success) {
      console.log("[NativePush] Token refreshed and registered")
      this.dispatch("registered", { detail: { token, platform: getPlatform() } })
    } else {
      console.error("[NativePush] Token refresh registration failed:", result.error)
      this.dispatch("registrationError", { detail: { error: result.error } })
    }
  }

  /**
   * Unregister push notifications
   */
  async unregister() {
    if (!this.isNative) return

    this.setLoading(true)

    try {
      const result = await unregisterToken({
        endpoint: this.unregisterUrlValue
      })

      if (result.success) {
        this.setSuccess(this.i18nDisabledValue)
        this.setToggleState(false)
        this.dispatch("unregistered", { detail: { platform: getPlatform() } })
      } else {
        throw new Error(result.error)
      }
    } catch (error) {
      console.error("[NativePush] Unregister error:", error)
      this.setError(error.message)
      this.setToggleState(true) // Revert toggle on error
    } finally {
      this.setLoading(false)
    }
  }

  /**
   * Handle incoming notification
   */
  handleNotification(data) {
    console.log("[NativePush] Notification received:", data)
    this.dispatch("notification", { detail: data, bubbles: true })

    if (data.url) {
      Turbo.visit(data.url)
    }
  }

  // ============================================================================
  // UI State Management
  // ============================================================================

  setToggleState(checked, disabled = false) {
    if (!this.hasToggleTarget) return
    this.toggleTarget.checked = checked
    this.toggleTarget.disabled = disabled
  }

  setStatus(message) {
    if (!this.hasStatusTarget || !message) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("text-red-500", "text-green-600")
  }

  setLoading(loading) {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = loading
      this.buttonTarget.classList.toggle("opacity-50", loading)
    }

    if (this.hasStatusTarget && loading && this.i18nLoadingValue) {
      this.statusTarget.textContent = this.i18nLoadingValue
      this.statusTarget.classList.remove("text-red-500", "text-green-600")
    }
  }

  setSuccess(message) {
    if (!this.hasStatusTarget || !message) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.add("text-green-600")
    this.statusTarget.classList.remove("text-red-500")
  }

  setError(message) {
    if (!this.hasStatusTarget || !message) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.add("text-red-500")
    this.statusTarget.classList.remove("text-green-600")
  }
}
